import { PdfImportCountry } from './pdf-import-country';
import React from 'react';
import { tr } from 'helper/translate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionSummary, AccordionDetails, Alert, Box, Button, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  emptyPdfImportRoom,
  PdfImportRequest,
  PdfImportRequestDay,
  PdfImportRequestRoom,
  pdfImportAddonCodes,
  pdfImportSeatings,
} from 'semshared/offer/pdf-import';

// Translation keys per seating / extra service code: `pdf-import-seating-<CODE>`, `pdf-import-addon-<CODE>`.
const seatingName = (code: string) => tr(`pdf-import-seating-${code}`);
const addonName = (code: string) => tr(`pdf-import-addon-${code}`);
const numberValue = (value: string): number | null => (value === '' ? null : Number(value));

function RequestField(props: {
  label: string;
  value: string | number | null;
  onChange?: (value: string) => void;
  numeric?: boolean;
  time?: boolean;
  options?: Record<string, string>;
}) {
  if (!props.onChange)
    return (
      <Box>
        <Typography variant="subtitle2">{props.label}</Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
          {props.value === null || props.value === '' ? tr('pdf-import-unknown') : props.options?.[props.value] || props.value}
        </Typography>
      </Box>
    );
  return (
    <TextField
      fullWidth
      size="small"
      label={props.label}
      value={props.value ?? ''}
      placeholder={tr('pdf-import-unknown')}
      helperText={props.value === null || props.value === '' ? tr('pdf-import-unknown') : undefined}
      type={props.numeric ? 'number' : props.time ? 'time' : 'text'}
      inputProps={props.numeric ? { min: 0, step: 1 } : undefined}
      InputLabelProps={props.time ? { shrink: true } : undefined}
      select={!!props.options}
      onChange={event => props.onChange!(event.target.value)}
    >
      {props.options && [
        <MenuItem key="" value="">
          {tr('pdf-import-unknown')}
        </MenuItem>,
        ...Object.entries(props.options).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        )),
      ]}
    </TextField>
  );
}

export function PdfImportBilling(props: { request: PdfImportRequest; onChange?: (request: PdfImportRequest) => void }) {
  return (
    <Grid container spacing={2}>
      {(['company', 'firstname', 'lastname', 'address', 'zip', 'city', 'country'] as const)
        .map(key => [key, tr(`pdf-import-billing-${key}`)] as const)
        .map(([key, label]) => (
          <Grid item xs={12} sm={6} key={key}>
            {key === 'country' && props.onChange ? (
              <PdfImportCountry
                label={label}
                value={props.request.billing.country}
                onChange={country => props.onChange!({ ...props.request, billing: { ...props.request.billing, country } })}
              />
            ) : (
              <RequestField
                label={label}
                value={props.request.billing[key]}
                onChange={
                  props.onChange
                    ? value =>
                        props.onChange!({
                          ...props.request,
                          billing: { ...props.request.billing, [key]: key === 'country' ? value.toUpperCase() : value },
                        })
                    : undefined
                }
              />
            )}
          </Grid>
        ))}
    </Grid>
  );
}

export function PdfImportRequestDetails(props: {
  request: PdfImportRequest;
  onChange?: (request: PdfImportRequest) => void;
  showComment?: boolean;
  startDate?: string;
  endDate?: string;
}) {
  const changeDay = (index: number, change: (day: PdfImportRequestDay) => PdfImportRequestDay) =>
    props.onChange?.({ ...props.request, days: props.request.days.map((day, i) => (i === index ? change(day) : day)) });
  const roomField = (index: number, roomIndex: number, key: keyof PdfImportRequestRoom, numeric = false) =>
    props.onChange
      ? (value: string) =>
          changeDay(index, day => ({
            ...day,
            rooms: day.rooms.map((room, i) => (i === roomIndex ? { ...room, [key]: numeric ? numberValue(value) : value } : room)),
          }))
      : undefined;
  return (
    <Stack spacing={2}>
      {props.showComment !== false && (
        <RequestField
          label={tr('pdf-import-request-comment')}
          value={props.request.comment}
          onChange={props.onChange ? value => props.onChange!({ ...props.request, comment: value }) : undefined}
        />
      )}
      {props.request.days.map((day, index) => (
        <Accordion
          key={day.date}
          defaultExpanded={!props.onChange || !!((props.startDate && day.date < props.startDate) || (props.endDate && day.date > props.endDate))}
          disableGutters
          elevation={0}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle1">{day.date.split('-').reverse().join('.')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {tr('pdf-import-day-summary', {
                  guests: day.guests ?? tr('pdf-import-unknown'),
                  single: day.singleRooms ?? tr('pdf-import-unknown'),
                  double: day.doubleRooms ?? tr('pdf-import-unknown'),
                })}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack useFlexGap spacing={2}>
              {props.onChange && ((props.startDate && day.date < props.startDate) || (props.endDate && day.date > props.endDate)) && (
                <Alert
                  severity="warning"
                  action={
                    <Button onClick={() => props.onChange!({ ...props.request, days: props.request.days.filter(item => item.date !== day.date) })}>
                      {tr('pdf-import-day-remove')}
                    </Button>
                  }
                >
                  {tr('pdf-import-day-outside')}
                </Alert>
              )}
              <Grid container spacing={2}>
                {(
                  [
                    ['guests', tr('pdf-import-guests')],
                    ['singleRooms', tr('pdf-import-single-rooms')],
                    ['doubleRooms', tr('pdf-import-double-rooms')],
                  ] as const
                ).map(([key, label]) => (
                  <Grid item xs={12} sm={4} key={key}>
                    <RequestField
                      numeric
                      label={label}
                      value={day[key]}
                      onChange={props.onChange ? value => changeDay(index, current => ({ ...current, [key]: numberValue(value) })) : undefined}
                    />
                  </Grid>
                ))}
              </Grid>
              <RequestField
                label={tr('pdf-import-rooms')}
                value={day.roomsStatus === 'UNKNOWN' ? '' : 'KNOWN'}
                options={{ KNOWN: tr(day.rooms.length ? 'pdf-import-from-pdf' : 'pdf-import-no-rooms') }}
                onChange={props.onChange ? value => changeDay(index, current => ({ ...current, roomsStatus: value || 'UNKNOWN' })) : undefined}
              />
              {day.rooms.map((room, roomIndex) => (
                <Stack useFlexGap key={roomIndex} spacing={1} sx={{ borderLeft: 2, borderColor: 'divider', pl: 2 }}>
                  <Typography variant="subtitle1">{tr('pdf-import-room', { n: roomIndex + 1 })}</Typography>
                  <Grid container spacing={2}>
                    {(
                      [
                        ['number', true],
                        ['name', false],
                        ['from', false],
                        ['to', false],
                        ['participants', true],
                        ['minArea', true],
                        ['beamers', true],
                        ['flipcharts', true],
                        ['pinboards', true],
                      ] as const
                    ).map(([key, numeric]) => (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <RequestField
                          label={tr(`pdf-import-room-${key}`)}
                          value={room[key]}
                          numeric={numeric}
                          time={key === 'from' || key === 'to'}
                          onChange={roomField(index, roomIndex, key, numeric)}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12} sm={6}>
                      <RequestField
                        label={tr('pdf-import-seating')}
                        value={room.seating}
                        options={Object.fromEntries(pdfImportSeatings.map(value => [value, seatingName(value)]))}
                        onChange={roomField(index, roomIndex, 'seating')}
                      />
                    </Grid>
                    {(
                      [
                        ['airConditioning', tr('pdf-import-room-airConditioning')],
                        ['daylight', tr('pdf-import-room-daylight')],
                      ] as const
                    ).map(([key, label]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <RequestField
                          label={label}
                          value={room[key]}
                          options={{ YES: tr('pdf-import-yes'), NO: tr('pdf-import-no') }}
                          onChange={roomField(index, roomIndex, key)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {props.onChange && (
                    <Button
                      size="small"
                      onClick={() => changeDay(index, current => ({ ...current, rooms: current.rooms.filter((_, i) => i !== roomIndex) }))}
                    >
                      {tr('pdf-import-room-remove')}
                    </Button>
                  )}
                </Stack>
              ))}
              {props.onChange && (
                <Button
                  onClick={() => changeDay(index, current => ({ ...current, roomsStatus: 'KNOWN', rooms: [...current.rooms, emptyPdfImportRoom()] }))}
                >
                  {tr('pdf-import-room-add')}
                </Button>
              )}
              <RequestField
                label={tr('pdf-import-addons')}
                value={day.addonsStatus === 'UNKNOWN' ? '' : 'KNOWN'}
                options={{ KNOWN: tr(day.addons.length ? 'pdf-import-from-pdf' : 'pdf-import-no-addons') }}
                onChange={props.onChange ? value => changeDay(index, current => ({ ...current, addonsStatus: value || 'UNKNOWN' })) : undefined}
              />
              {day.addons.map((addon, addonIndex) => {
                const update = (key: 'description' | 'lookupCode' | 'count', numeric = false) =>
                  props.onChange
                    ? (value: string) =>
                        changeDay(index, current => ({
                          ...current,
                          addons: current.addons.map((entry, i) =>
                            i === addonIndex ? { ...entry, [key]: numeric ? numberValue(value) : value } : entry,
                          ),
                        }))
                    : undefined;
                return (
                  <Grid container spacing={2} key={addonIndex}>
                    <Grid item xs={12} sm={5}>
                      <RequestField label={tr('pdf-import-addon-description')} value={addon.description} onChange={update('description')} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <RequestField
                        label={tr('pdf-import-addon-type')}
                        value={addon.lookupCode}
                        options={Object.fromEntries(pdfImportAddonCodes.map(value => [value, addonName(value)]))}
                        onChange={update('lookupCode')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <RequestField label={tr('pdf-import-quantity')} numeric value={addon.count} onChange={update('count', true)} />
                    </Grid>
                    {props.onChange && (
                      <Grid item xs={12}>
                        <Button
                          size="small"
                          onClick={() => changeDay(index, current => ({ ...current, addons: current.addons.filter((_, i) => i !== addonIndex) }))}
                        >
                          {tr('pdf-import-addon-remove')}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                );
              })}
              {props.onChange && (
                <Button
                  onClick={() =>
                    changeDay(index, current => ({
                      ...current,
                      addonsStatus: 'KNOWN',
                      addons: [...current.addons, { description: '', lookupCode: '', count: null }],
                    }))
                  }
                >
                  {tr('pdf-import-addon-add')}
                </Button>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}
